import type React from 'react';
import { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useViewport } from '../../../hooks/useViewport';
import { type ILegalDocument } from '../../../interface/IElection';
import { CloseIcon, DocumentIcon, SpinnerIcon } from './icons';
import {
	docBodyInnerStyles,
	docBodyStyles,
	docCloseButtonStyles,
	docEyebrowStyles,
	docHeaderStyles,
	docHeaderTextStyles,
	docHeaderTileStyles,
	docRetryButtonStyles,
	docStateStyles,
	docTitleStyles,
	getDocContainerStyles,
	getDocOverlayStyles,
} from './styled';

// react-markdown + remark-gfm + rehype-sanitize live behind this lazy import so
// they land in their own chunk and never touch the initial bundle.
const MarkdownDocument = lazy(async () => await import('./MarkdownDocument'));

interface LegalDocumentModalProps {
	isOpen: boolean;
	title: string;
	document?: ILegalDocument;
	loading?: boolean;
	error?: unknown;
	onRetry: () => void;
	onClose: () => void;
}

const formatDate = (iso?: string): string => {
	if (!iso) return '';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '';
	return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const LoadingState: React.FC = () => (
	<div style={docStateStyles}>
		<SpinnerIcon size={28} />
		<span>Loading document…</span>
	</div>
);

const LegalDocumentModal: React.FC<LegalDocumentModalProps> = ({
	isOpen,
	title,
	document: doc,
	loading,
	error,
	onRetry,
	onClose,
}) => {
	const { isSm } = useViewport();
	const containerRef = useRef<HTMLDivElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);

	// Scroll-lock: restore the PREVIOUS value (not '') on close so dismissing
	// this dialog does not unlock the consent modal underneath it.
	useEffect(() => {
		if (!isOpen) return;
		const previousOverflow = window.document.body.style.overflow;
		window.document.body.style.overflow = 'hidden';
		return () => {
			window.document.body.style.overflow = previousOverflow;
		};
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) closeButtonRef.current?.focus();
	}, [isOpen]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				onClose();
				return;
			}
			if (e.key !== 'Tab') return;
			const focusable = containerRef.current?.querySelectorAll<HTMLElement>(
				'button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'
			);
			if (!focusable || focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (e.shiftKey && window.document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && window.document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		},
		[onClose]
	);

	useEffect(() => {
		if (!isOpen) return;
		window.document.addEventListener('keydown', handleKeyDown);
		return () => window.document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, handleKeyDown]);

	if (!isOpen) return null;

	const eyebrowParts = [doc?.version, formatDate(doc?.publishedAt) ? `Updated ${formatDate(doc?.publishedAt)}` : ''].filter(
		Boolean
	);

	const renderBody = () => {
		if (doc?.body) {
			return (
				<div style={docBodyInnerStyles}>
					<Suspense fallback={<LoadingState />}>
						<MarkdownDocument body={doc.body} />
					</Suspense>
				</div>
			);
		}
		if (loading) return <LoadingState />;
		return (
			<div style={docStateStyles}>
				<span>{error ? 'We couldn’t load this document.' : 'This document isn’t available right now.'}</span>
				<button
					type='button'
					style={docRetryButtonStyles}
					onClick={onRetry}
				>
					Try again
				</button>
			</div>
		);
	};

	// Portal to <body> so the dialog escapes the gated form's stacking context
	// (a blurred/relative wrapper with a low z-index) and reliably sits above the
	// app header and the consent modal underneath it.
	return createPortal(
		<div
			style={getDocOverlayStyles(isSm)}
			role='presentation'
			onClick={onClose}
		>
			<div
				ref={containerRef}
				role='dialog'
				aria-modal='true'
				aria-labelledby='legal-document-title'
				tabIndex={-1}
				style={getDocContainerStyles(isSm)}
				onClick={(e) => e.stopPropagation()}
			>
				<div style={docHeaderStyles}>
					<div style={docHeaderTileStyles}>
						<DocumentIcon />
					</div>
					<div style={docHeaderTextStyles}>
						{eyebrowParts.length > 0 && <p style={docEyebrowStyles}>{eyebrowParts.join(' · ')}</p>}
						<h2
							id='legal-document-title'
							style={docTitleStyles}
						>
							{title}
						</h2>
					</div>
					<button
						ref={closeButtonRef}
						type='button'
						style={docCloseButtonStyles}
						onClick={onClose}
						aria-label='Close document'
					>
						<CloseIcon />
					</button>
				</div>

				<div style={docBodyStyles}>{renderBody()}</div>
			</div>
		</div>,
		document.body
	);
};

export default LegalDocumentModal;
